import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaStar, FaCheck, FaBan, FaFilter } from 'react-icons/fa';

const emptyForm = {
  name: '',
  title: '',
  content: '',
  rating: 5,
  sort_order: 0,
  is_active: true,
};

export default function AdminTestimonials() {
  useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved, admin, user

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await api.get('/testimonials/all');
      setTestimonials(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Yorumlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredTestimonials = testimonials.filter((item) => {
    if (filter === 'pending') return !item.is_approved;
    if (filter === 'approved') return item.is_approved;
    if (filter === 'admin') return item.source === 'admin';
    if (filter === 'user') return item.source === 'user';
    return true;
  });

  const pendingCount = testimonials.filter(t => !t.is_approved).length;

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setImage(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      title: item.title || '',
      content: item.content || '',
      rating: item.rating || 5,
      sort_order: item.sort_order || 0,
      is_active: item.is_active !== undefined ? item.is_active : true,
    });
    setImage(null);
    setImagePreview(item.image_path ? getImageUrl(item.image_path) : null);
    setShowModal(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (image) {
        formData.append('image', image);
      } else if (editing && editing.image_path) {
        formData.append('existing_image', editing.image_path);
      }

      if (editing) {
        await api.put(`/testimonials/${editing.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Yorum güncellendi');
      } else {
        await api.post('/testimonials', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Yorum eklendi');
      }
      setShowModal(false);
      fetchTestimonials();
    } catch (err) {
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/testimonials/${id}/approve`);
      toast.success('Yorum onaylandı');
      fetchTestimonials();
    } catch (err) {
      toast.error('Onaylama sırasında hata oluştu');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.put(`/testimonials/${id}/reject`);
      toast.success('Yorum reddedildi');
      fetchTestimonials();
    } catch (err) {
      toast.error('Reddetme sırasında hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success('Yorum silindi');
      fetchTestimonials();
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FaStar key={i} style={{ color: i < rating ? '#f5a623' : '#ddd', fontSize: 14 }} />
    ));
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>
            Müşteri Yorumları
            {pendingCount > 0 && (
              <span className="badge badge-warning" style={{ marginLeft: 10, fontSize: 12 }}>
                {pendingCount} onay bekliyor
              </span>
            )}
          </h2>
          <button className="btn btn-primary" onClick={openAdd}>
            <FaPlus /> Yeni Yorum
          </button>
        </div>

        {/* Filter tabs */}
        <div className="admin-filter-tabs">
          <button className={`filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            Tümü ({testimonials.length})
          </button>
          <button className={`filter-tab ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
            Onay Bekleyen ({pendingCount})
          </button>
          <button className={`filter-tab ${filter === 'approved' ? 'active' : ''}`} onClick={() => setFilter('approved')}>
            Onaylı ({testimonials.filter(t => t.is_approved).length})
          </button>
          <button className={`filter-tab ${filter === 'user' ? 'active' : ''}`} onClick={() => setFilter('user')}>
            Kullanıcı ({testimonials.filter(t => t.source === 'user').length})
          </button>
          <button className={`filter-tab ${filter === 'admin' ? 'active' : ''}`} onClick={() => setFilter('admin')}>
            Admin ({testimonials.filter(t => t.source === 'admin').length})
          </button>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Görsel</th>
              <th>Ad Soyad</th>
              <th>Puan</th>
              <th>Yorum</th>
              <th>Kaynak</th>
              <th>Onay</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredTestimonials.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center' }}>
                  Bu filtrede yorum bulunmuyor
                </td>
              </tr>
            ) : (
              filteredTestimonials.map((item) => (
                <tr key={item.id} className={!item.is_approved ? 'row-pending' : ''}>
                  <td>
                    {item.image_path && (
                      <img
                        src={getImageUrl(item.image_path)}
                        alt={item.name}
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    )}
                  </td>
                  <td>{item.name}</td>
                  <td>{renderStars(item.rating)}</td>
                  <td>{item.content && item.content.length > 50 ? item.content.substring(0, 50) + '...' : item.content}</td>
                  <td>
                    <span className={`badge ${item.source === 'user' ? 'badge-info' : 'badge-secondary'}`}>
                      {item.source === 'user' ? 'Kullanıcı' : 'Admin'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${item.is_approved ? 'badge-success' : 'badge-warning'}`}>
                      {item.is_approved ? 'Onaylı' : 'Bekliyor'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${item.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {item.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      {!item.is_approved && (
                        <button className="btn btn-success btn-sm" onClick={() => handleApprove(item.id)} title="Onayla">
                          <FaCheck />
                        </button>
                      )}
                      {item.is_approved && item.source === 'user' && (
                        <button className="btn btn-warning btn-sm" onClick={() => handleReject(item.id)} title="Reddet">
                          <FaBan />
                        </button>
                      )}
                      <button className="btn btn-primary btn-sm" onClick={() => openEdit(item)} title="Düzenle">
                        <FaEdit />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)} title="Sil">
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Yorum Düzenle' : 'Yeni Yorum'}</h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <label>Ad Soyad</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Unvan / Meslek</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Örn: Şirket Müdürü, İstanbul"
                />
              </div>
              <div className="form-group full-width">
                <label>Yorum</label>
                <textarea
                  value={form.content}
                  onChange={(e) => handleChange('content', e.target.value)}
                  required
                  style={{ minHeight: '100px' }}
                />
              </div>
              <div className="form-group">
                <label>Puan</label>
                <select
                  value={form.rating}
                  onChange={(e) => handleChange('rating', parseInt(e.target.value))}
                >
                  <option value={5}>5 Yıldız</option>
                  <option value={4}>4 Yıldız</option>
                  <option value={3}>3 Yıldız</option>
                  <option value={2}>2 Yıldız</option>
                  <option value={1}>1 Yıldız</option>
                </select>
              </div>
              <div className="form-group">
                <label>Görsel</label>
                <input type="file" accept="image/*" onChange={handleImageChange} />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="image-preview" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', marginTop: 10 }} />
                )}
              </div>
              <div className="form-group">
                <label>Sıralama</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Durum</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                  <span>{form.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
